import { useCallback, useMemo, useState } from 'react';

import { coerceFilterValues } from '#/modules/commonComponents/table/columnFilterUtils';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import { resolveInvStockPickerWarehouseFilter } from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';

const DEFAULT_PAGE_SIZE = 10;

export const INV_STOCK_PICKER_BASE_FILTERS = {
  status: 'ACTIVE',
  invStatus: 'AVAILABLE',
};

const BASE_FILTER_KEYS = new Set(Object.keys(INV_STOCK_PICKER_BASE_FILTERS));

function sanitizeUserWarehouseFilter(userWarehouseId, excludeWarehouseId) {
  if (!excludeWarehouseId) {
    return userWarehouseId;
  }
  const sanitized = coerceFilterValues(userWarehouseId)
    .filter((id) => String(id) !== String(excludeWarehouseId));
  return sanitized.length > 0 ? sanitized : undefined;
}

/**
 * 轉倉／出庫／調整／SO 配置庫存 picker 本地 listQuery（不寫 URL）。
 * @param {{ excludeWarehouseId?: string|null, mtrId?: string|null }} [options]
 */
function useInvStockPickerListQuery({
  excludeWarehouseId = null,
  mtrId = null,
} = {}) {
  const [pageState, setPageState] = useState({
    excludeWarehouseId: null,
    mtrId: null,
    page: 1,
  });
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [keyword, setKeywordState] = useState('');
  const [sort, setSortState] = useState([]);
  const [userFilters, setUserFilters] = useState({});

  const scopeMatches = pageState.excludeWarehouseId === excludeWarehouseId
    && pageState.mtrId === mtrId;
  const page = scopeMatches ? pageState.page : 1;

  const listQuery = useMemo(() => {
    const { warehouseId: userWarehouseId, ...restUserFilters } = userFilters;
    return {
      page,
      pageSize,
      keyword,
      sort,
      filters: {
        ...INV_STOCK_PICKER_BASE_FILTERS,
        ...(mtrId ? { mtrId } : {}),
        ...restUserFilters,
        ...resolveInvStockPickerWarehouseFilter(
          excludeWarehouseId,
          userWarehouseId,
        ),
      },
    };
  }, [page, pageSize, keyword, sort, userFilters, excludeWarehouseId, mtrId]);

  /** thead／status 用：warehouseId 只反映使用者選取（勿顯示 !=uuid）。 */
  const columnFilters = useMemo(() => ({
    ...listQuery.filters,
    warehouseId: sanitizeUserWarehouseFilter(
      userFilters.warehouseId,
      excludeWarehouseId,
    ),
  }), [listQuery.filters, userFilters.warehouseId, excludeWarehouseId]);

  const setPage = useCallback((nextPage) => {
    setPageState({ excludeWarehouseId, mtrId, page: nextPage });
  }, [excludeWarehouseId, mtrId]);

  const setPageSize = useCallback((nextPageSize) => {
    setPageSizeState(nextPageSize);
    setPageState({ excludeWarehouseId, mtrId, page: 1 });
  }, [excludeWarehouseId, mtrId]);

  const setKeyword = useCallback((nextKeyword) => {
    setKeywordState(nextKeyword ?? '');
    setPageState({ excludeWarehouseId, mtrId, page: 1 });
  }, [excludeWarehouseId, mtrId]);

  const setFilter = useCallback((key, value) => {
    if (BASE_FILTER_KEYS.has(key)) {
      return;
    }
    // SO 配置：鎖定 mtrId，禁止表頭覆寫
    if (key === 'mtrId' && mtrId) {
      return;
    }

    let nextValue = value;
    if (key === 'warehouseId' && excludeWarehouseId) {
      nextValue = sanitizeUserWarehouseFilter(value, excludeWarehouseId);
    }

    setUserFilters((prev) => mergeFilterUpdate(prev, key, nextValue));
    setPageState({ excludeWarehouseId, mtrId, page: 1 });
  }, [excludeWarehouseId, mtrId]);

  const setSort = useCallback((nextSort) => {
    setSortState(nextSort ?? []);
    setPageState({ excludeWarehouseId, mtrId, page: 1 });
  }, [excludeWarehouseId, mtrId]);

  const removeSortAt = useCallback((index) => {
    setSortState((prev) => (prev ?? []).filter((_, sortIndex) => sortIndex !== index));
    setPageState({ excludeWarehouseId, mtrId, page: 1 });
  }, [excludeWarehouseId, mtrId]);

  const resetQuery = useCallback(() => {
    setKeywordState('');
    setSortState([]);
    setUserFilters({});
    setPageSizeState(DEFAULT_PAGE_SIZE);
    setPageState({ excludeWarehouseId, mtrId, page: 1 });
  }, [excludeWarehouseId, mtrId]);

  return {
    listQuery,
    columnFilters,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  };
}

export default useInvStockPickerListQuery;
